import { useEffect, useState } from 'react';
import { useTimesheetStore } from '../../../store/timesheetStore';
import { useProjectStore } from '../../../store/projectStore';
import { useTaskStore } from '../../../store/taskStore';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/Dialog';
import toast from 'react-hot-toast';
import { Clock, Trash2, Pencil } from 'lucide-react';

export function Timesheets() {
  const { timesheets, fetchTimesheets, createTimesheet, updateTimesheet, deleteTimesheet, isLoading } = useTimesheetStore();
  const { projects, fetchProjects } = useProjectStore();
  const { tasks, fetchTasks } = useTaskStore();

  const [isLogging, setIsLogging] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  const [newLog, setNewLog] = useState({
    projectId: '',
    taskId: '',
    date: new Date().toISOString().split('T')[0],
    hours: '',
    description: ''
  });

  const resetLog = () => setNewLog({
    projectId: '', taskId: '', date: new Date().toISOString().split('T')[0], hours: '', description: '',
  });

  const openCreate = () => { setEditingId(null); resetLog(); setIsLogging(true); };

  const openEdit = (t: any) => {
    setEditingId(t.id);
    setNewLog({
      projectId: t.project_id ?? '',
      taskId: t.task_id ?? '',
      date: t.date ? String(t.date).split('T')[0] : new Date().toISOString().split('T')[0],
      hours: String(t.hours ?? ''),
      description: t.description ?? '',
    });
    setIsLogging(true);
  };

  useEffect(() => {
    fetchTimesheets(selectedProjectId);
  }, [selectedProjectId, fetchTimesheets]);

  useEffect(() => {
    if (projects.length === 0) fetchProjects();
  }, [projects.length, fetchProjects]);

  useEffect(() => {
    if (newLog.projectId) {
      fetchTasks(newLog.projectId);
    }
  }, [newLog.projectId, fetchTasks]);

  const handleLogTime = async () => {
    try {
      const hours = parseFloat(newLog.hours);
      if (isNaN(hours) || hours <= 0 || hours > 24) {
        return toast.error('Hours must be between 0 and 24.');
      }
      
      const selectedDate = new Date(newLog.date);
      const today = new Date();
      // Reset time to compare just dates properly if needed, but a simple check:
      if (selectedDate > today) {
        return toast.error('Cannot log time for future dates.');
      }

      if (!newLog.projectId) {
        return toast.error('Please select a project.');
      }

      const payload = {
        projectId: newLog.projectId,
        taskId: newLog.taskId || undefined,
        date: newLog.date,
        hours,
        description: newLog.description,
      };

      if (editingId) {
        await updateTimesheet(editingId, payload);
        toast.success('Time entry updated!');
      } else {
        await createTimesheet(payload);
        toast.success('Time logged successfully!');
      }
      setIsLogging(false);
      setEditingId(null);
      resetLog();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const totalHours = timesheets.reduce((acc, t) => acc + Number(t.hours), 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">Time <span className="text-foreground">Sheets</span></h1>
          <p className="text-muted-foreground mt-1">Log and track time spent on projects and tasks</p>
        </div>
        <div className="flex gap-3">
          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="px-4 py-2 bg-card border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary font-semibold text-sm"
          >
            <option value="">All Projects</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <Button variant="unstyled" size="unstyled"
            onClick={openCreate}
            className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all shadow-lg shadow-primary/20 flex items-center gap-2"
          >
            <Clock className="size-4" />
            Log Time
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <p className="text-sm font-semibold text-muted-foreground mb-1">Total Hours Logged</p>
          <p className="text-foreground font-display text-metric">{totalHours.toFixed(2)}h</p>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-muted-foreground bg-muted/50 border-b border-border">
              <tr>
                <th className="px-6 py-4 font-semibold">Date</th>
                <th className="px-6 py-4 font-semibold">Project / Task</th>
                <th className="px-6 py-4 font-semibold">Description</th>
                <th className="px-6 py-4 font-semibold">Hours</th>
                <th className="px-6 py-4 font-semibold text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && timesheets.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">Loading timesheets...</td>
                </tr>
              ) : timesheets.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Clock className="size-12 text-muted-foreground/30 mx-auto mb-3" />
                    <p className="text-foreground font-semibold">No time logged yet</p>
                    <p className="text-muted-foreground text-sm mt-1">Click "Log Time" to add your first entry.</p>
                  </td>
                </tr>
              ) : (
                timesheets.map((t) => (
                  <tr key={t.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="px-6 py-4 font-medium text-foreground whitespace-nowrap">
                      {new Date(t.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-foreground">{t.project_name}</p>
                      {t.task_name && <p className="text-xs text-muted-foreground mt-0.5">{t.task_name}</p>}
                    </td>
                    <td className="px-6 py-4 text-muted-foreground max-w-md truncate">
                      {t.description || '-'}
                    </td>
                    <td className="px-6 py-4 font-semibold text-foreground">
                      {Number(t.hours).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="unstyled"
                          size="unstyled"
                          onClick={() => openEdit(t)}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                          aria-label="Edit time entry"
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="unstyled"
                          size="unstyled"
                          onClick={async () => {
                            if (window.confirm('Delete this time entry?')) {
                              try {
                                await deleteTimesheet(t.id);
                                toast.success('Deleted successfully');
                              } catch (e: any) {
                                toast.error(e.message);
                              }
                            }
                          }}
                          className="p-2 text-muted-foreground hover:text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
                          aria-label="Delete time entry"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={isLogging} onOpenChange={(o) => { setIsLogging(o); if (!o) setEditingId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Time Entry' : 'Log Time'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2">Date *</label>
              <Input
                type="date"
                value={newLog.date}
                max={new Date().toISOString().split('T')[0]} // HTML5 validation edge case
                onChange={e => setNewLog(p => ({ ...p, date: e.target.value }))}
                className="w-full bg-background font-semibold"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2">Project *</label>
              <select
                value={newLog.projectId}
                onChange={e => setNewLog(p => ({ ...p, projectId: e.target.value, taskId: '' }))}
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground font-semibold"
              >
                <option value="">Select Project</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            {newLog.projectId && tasks.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-2">Task (Optional)</label>
                <select
                  value={newLog.taskId}
                  onChange={e => setNewLog(p => ({ ...p, taskId: e.target.value }))}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground font-semibold"
                >
                  <option value="">Select Task</option>
                  {tasks.map(t => (
                    <option key={t.id} value={t.id}>{t.title}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2">Hours *</label>
              <Input
                type="number"
                min="0.1"
                max="24"
                step="0.1"
                value={newLog.hours}
                onChange={e => setNewLog(p => ({ ...p, hours: e.target.value }))}
                placeholder="e.g. 2.5"
                className="w-full bg-background font-semibold"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-muted-foreground mb-2">Description</label>
              <textarea
                value={newLog.description}
                onChange={e => setNewLog(p => ({ ...p, description: e.target.value }))}
                rows={3}
                placeholder="What did you work on?"
                className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground resize-none"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="unstyled" size="unstyled" onClick={() => setIsLogging(false)} className="px-4 py-2 font-semibold text-foreground hover:bg-muted rounded-lg">
                Cancel
              </Button>
              <Button variant="unstyled" size="unstyled" onClick={handleLogTime} className="px-6 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg shadow-lg shadow-primary/20">
                Save Entry
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
